<script>
//  HAM-889
import { BCardTitle, BCard, BButton, BBadge, BRow, BCol } from 'bootstrap-vue'
import {SubstanceApi} from "@core/api";
import AppCollapseItem from "@core/components/app-collapse/AppCollapseItem.vue";
import AppCollapse from "@core/components/app-collapse/AppCollapse.vue";
import {
    AdditionalMaterial, MaxConcentration, MixtureCategory, MaterialCategory, ConcentrationRange, Exemption,
    CandidateList, PropertySelectField, PropertyNumberWithUnitField, PropertyYesNoQuestionsField,
    FcmRequirementsCheckField,
} from "@/views/product/components/product-card/composition/components";
import {ProductPropertyType} from "@core/domain/substance";
import {SubstanceLists} from "@/libs/substance";
import Overlay from "@core/components/common/Overlay.vue";

export default {
    components: {
        Overlay,
        AppCollapseItem, AppCollapse, BCardTitle, BCard, BButton, BBadge, AdditionalMaterial, MaxConcentration,
        MixtureCategory, MaterialCategory, ConcentrationRange, Exemption, BRow, BCol, CandidateList, PropertySelectField,
        PropertyNumberWithUnitField, PropertyYesNoQuestionsField, FcmRequirementsCheckField
    },
    props: {
        compactView: {
            type: Boolean,
            default: false
        },
        readOnly: {
            type: Boolean,
            default: true
        },
        readOnlyMode: {
            type: String,
            default: 'stacked'
        },
        substance: {
            type: Object,
            default() {
                return {
                    substance: {
                        title: ''
                    },
                    details: {},
                    properties: [],
                    lists: []
                };
            }
        },
        substanceLists: {
            type: Array,
            default() {
                return [];
            }
        }
    },
    data() {
        return {
            propsConfig: {
                map: {
                    list: {},
                    regulation: {}
                },
                properties: {},
            },
            commonProperties: [],
            activeProps: [],
            types: ProductPropertyType,
            activeLists: [],
            listProperties: [],
            productProperties: Array.isArray(this.substance.properties) ? {} : this.substance.properties,

            propertyConfigLoaded: false,
            showLoading: true
        };
    },
    methods: {
        exclude() {
            this.$emit('exclude', this.substance);
        },
        change(property, value, list = null) {
            this.setPropertyValue(property, value, list);
            this.emit();
        },
        candidateListChange(propertyValueMap) {
            const lists = [];
            if (this.isCandidateListActive) {
                lists.push(this.candidateList);
            }
            lists.forEach(list => {
                Object.keys(propertyValueMap).forEach(property => {
                    this.setPropertyValue(
                        this.propsConfig.properties[property],
                        propertyValueMap[property],
                        list
                    );
                });
            });

            this.emit();
        },
        setPropertyValue(property, value, list = null) {
            if (property.listSpecific) {
                if (null === list) {
                    throw new Error('Substance list should be presented to set specific value')
                }
                const items = this.productProperties.hasOwnProperty(property.type) ? this.productProperties[property.type] : [];
                const itemIndex = items.findIndex(item => item.list.id === list.id);

                if (itemIndex > -1) {
                    items[itemIndex].value = value;
                } else {
                    items.push({
                        list: list.toDTO(),
                        value: value
                    });
                }

                this.productProperties[property.type] = items;
            } else {
                this.productProperties[property.type] = value;
            }
        },
        propertyValue(property, list = null) {
            if (property.listSpecific) {
                if (null === list) {
                    throw new Error('Substance list should be presented to set specific value')
                }
                const propertyValues = this.productProperties.hasOwnProperty(property.type) ? this.productProperties[property.type] : [];
                const itemIndex = propertyValues.findIndex(item => item.list.id === list.id);
                return itemIndex > -1 ? propertyValues[itemIndex].value : null;
            }
            return this.productProperties[property.type];
        },
        emit() {
            this.$emit('change', this.substance, this.productProperties);
        },
        selectPropertiesToDisplay() {
            if (!this.propertyConfigLoaded) {
                return;
            }

            if (Object.keys(this.propsConfig.properties).length === 0) {
                this.showLoading = false;
                return;
            }
            if (this.substance.lists.length === 0) {
                this.showLoading = false;
                return;
            }

            const propsKeys = Object.keys(this.propsConfig.properties);
            const substanceListIds = this.substance.lists.map(l => l.id);
            this.activeLists = this.substanceLists
                .filter(list => substanceListIds.includes(list.id) && this.propsConfig.map.list.hasOwnProperty(list.id));

            //  if lists are not included or there is no properties for them skip rendering
            if (this.activeLists.length === 0) {
                this.showLoading = false
                return;
            }

            const takenProps = [];
            const hasReach = this.isCandidateListActive;

            if (hasReach) {
                takenProps.push(ProductPropertyType.ADDITIONAL_MATERIAL);
                takenProps.push(ProductPropertyType.MATERIAL_CATEGORY);
                takenProps.push(ProductPropertyType.MIXTURE_CATEGORY);
            }

            //  process common only if there is sevaral lists
            if (this.activeLists.length > 1) {
                //  process common properties
                this.commonProperties = propsKeys.map((property) => {
                    const propertyConfig = this.propsConfig.properties[property]
                    propertyConfig.type = property;

                    if (propertyConfig.listSpecific) {
                        return undefined;
                    }

                    //  cand list block
                    if (hasReach && this.propsConfig.map.list[1].includes(property)) {
                        if (property !== 'max_concentration') {
                            return undefined;
                        }
                    }
                    //  end cand list block

                    if (this.activeLists.filter(l => this.propsConfig.map.list[l.id].includes(property)).length === this.activeLists.length) {
                        takenProps.push(property);
                        return propertyConfig;
                    }

                    return undefined;
                }).filter(v => v !== undefined);
            }

            const listProperties = [];
            this.activeLists.forEach(list => {
                if (list === this.candidateList) {
                    return;
                }

                const availableListProperties = this.propsConfig.map.list[list.id]
                    .filter(property => !takenProps.includes(property));

                if (availableListProperties.length > 0) {
                    listProperties.push({
                        list: list,
                        properties: availableListProperties.map(property => {
                            if (property === ProductPropertyType.MAX_CONCENTRATION_RANGE) {
                                this.propsConfig.properties[property].data = list.concentrationRanges;
                            }
                            return this.propsConfig.properties[property];
                        }),
                    })
                }
            });

            this.listProperties = listProperties;

            this.$nextTick(() => {
                this.showLoading = false;
            });
        },
        isCommon(property) {
            return this.commonProperties.find(p => p.type === property) !== undefined;
        },
    },
    computed: {
        candidateListTitle() {
            return this.candidateList.title;
        },
        isCandidateListActive() {
            return this.candidateList !== undefined;
        },
        candidateList() {
            return this.activeLists.find(l => l.key === SubstanceLists.CANDIDATE_LIST);
        },
        includedSubstanceLists() {
            return this.substance.lists;
        },
        hasPropertiesToRender() {
            return this.commonProperties.length > 0
                || this.listProperties.length > 0
                || this.isCandidateListActive;
        }
    },
    created() {
        SubstanceApi.getProductPropertyData().then(r => {
            this.propsConfig = r.data;
            this.propertyConfigLoaded = true;
            this.selectPropertiesToDisplay();
        });
    },
    watch: {
        substanceLists() {
            this.selectPropertiesToDisplay();
        },
        includedSubstanceLists() {
            this.selectPropertiesToDisplay();
        },
        'substance.lists.length'() {
            this.selectPropertiesToDisplay();
        },
        substance() {
            this.productProperties = Array.isArray(this.substance.properties) ? {} : this.substance.properties;
        },
    }
}
</script>

<template>
    <Overlay :show="showLoading">
        <b-card
            no-body
            bg-variant="default"
            border-variant="light"
        >
            <div class="card-header bg-light pt-1 pl-1 pb-50">
                <b-card-title class="text-decoration-underline">{{ substance.substance.title }}</b-card-title>
                <div class="text-right">
                    <b-badge class="mr-1" v-if="substance.substance.cas !== '-'">CAS: {{ substance.substance.cas }}</b-badge>
                    <b-badge class="mr-2" v-if="substance.substance.ec !== '-'">EC: {{ substance.substance.ec }}</b-badge>
                    <b-button
                        variant="gradient-danger"
                        class="btn-icon"
                        size="sm"
                        @click="exclude"
                    >
                        <feather-icon icon="MinusIcon" />
                    </b-button>
                </div>
            </div>
            <div v-if="hasPropertiesToRender">
                <div class="pl-1 pr-1 pt-1">
                    <component
                        v-for="property in commonProperties"
                        :is="property.name"
                        @input="(value) => change(property, value)"
                        v-bind="{readOnly: readOnly, compactView: compactView, config: property, value: propertyValue(property)}"
                    />
                </div>
                <AppCollapse
                    type="border"
                    class="m-1"
                >
                    <AppCollapseItem
                        v-if="isCandidateListActive"
                        :title="candidateListTitle"
                        is-visible
                        :is-collapsible="false"
                    >
                        <CandidateList
                            :candidate-list="candidateList"
                            :compact-view="compactView"
                            :read-only="readOnly"
                            :values="productProperties"
                            :properties="propsConfig.properties"
                            :substance="substance.substance"
                            @input="candidateListChange"
                        />
                    </AppCollapseItem>

                    <AppCollapseItem
                        v-for="listProps in listProperties"
                        :title="listProps.list.title"
                        is-visible
                        :is-collapsible="false"
                    >
                        <component
                            v-for="property in listProps.properties"
                            :is="property.name"
                            @input="(value) => change(property, value, listProps.list)"
                            v-bind="{readOnly: readOnly, compactView: compactView, list: listProps.list, substance: substance.substance, config: property, value: propertyValue(property, listProps.list)}"
                        />
                    </AppCollapseItem>
                </AppCollapse>
            </div>
        </b-card>
    </Overlay>
</template>
